import { toast } from "./components/Toasts";

export interface PlayerStruct {
  account_name: string;
  dispatch_url: string;
  magic: string;
}

export interface ClientMissionObjective {
  id: number;
  name: string;
  description: string;
  min_level_requirement: number;
  min_alcohol: number;
  missions_that_must_be_completed: number[];
  required_items: number[];
}

export interface ClientMission {
  id: number;
  name: string;
  description: string;
  objectives: ClientMissionObjective[];
  rewards: number[];
}

export interface SDItem {
  item_id: number;
  count: number;
}

export enum GameErrIDs {
  // both rest and websocket
  UnknownMessage = "UnknownMessage",
  // websocket only
  MessageForwardFailed = "MessageForwardFailed",
  WrongPhrase = "WrongPhrase",
  UnknownMob = "UnknownMob",
  UnKnownNPC = "UnKnownNPC",
  MobInteractionTooSoon = "MobInteractionTooSoon",
  // The game server has no player registered for this connection (stale join
  // or dropped dispatcher<->game link) — the user must log out and back in.
  PlayerNotFound = "PlayerNotFound",
  // rest only
  NoAccountFound = "NoAccountFound",
  AccountNameAlreadyExists = "AccountNameAlreadyExists",
  UnknownAccountError = "UnknownAccountError",
  FailedToSave = "FailedToSave",
  UserNamePasswordMismatch = "UserNamePasswordMismatch",
  InvalidAccountName = "InvalidAccountName",
  InvalidPassword = "InvalidPassword",
  CookieExpired = "CookieExpired",
}

export interface PlayerMove {
  zone_id: number;
  x_pos: number;
  y_pos: number;
}

export interface PlayerJoin {
  account_name: string;
  magic: string;
}

export interface SetPlayerProfile {
  sprite_id: number;
}

// Chat message types that match the server-side Rust definitions
// Values MUST match the Rust ChatType serde serialization (the variant names,
// uppercase) — the server sends these exact strings in ChatResponseMsg. They
// were previously "Say"/"Shout"/... which matched nothing, so every incoming
// message fell through to the default formatter (no [say]/[dm] marker at all).
export enum ChatType {
  Say = "SAY",
  Shout = "SHOUT",
  Broadcast = "BROADCAST",
  DM = "DIRECT",
}

// Used for direct messages (DMs)
export interface ChatReqMsg {
  chat_to: string;
  chat_string: string;
}

// Used for say, shout, broadcast
export interface ChatTypeSayRequest {
  chat_string: string;
  chat_type: ChatType;
}

// What client receives from any chat message
export interface ChatResponseMsg {
  chat_type: ChatType;
  chat_to: string;
  chat_from: string;
  chat_string: string;
}

export interface InteractNPC {
  npc_id: number;
}

export interface InteractNPCResponse {
  npc_id: number;
  mission_list: ClientMission[];
}

export interface AcceptMission {
  npc_id: number;
  mission_id: number;
}

export interface ObjectiveCompletionPhrase {
  mission_id: number;
  objective_id: number;
  phrase: string;
}

export interface MissionComplete {
  player: PlayerStruct;
  mission_id: number;
  objective_id: number;
  rewards_given: SDItem[];
  challenge_response: string; // server-gated custom success output (a clue)
}

export interface InteractMob {
  mob_id: number;
}

export interface InteractMobResponse {
  mob_id: number;
  player: PlayerStruct;
  item_list: SDItem[];
  err_id?: GameErrIDs;
  error_message?: string;
}

export interface InteractResource {
  resource_id: number;
}

export interface InteractResourceResponse {
  resource_id: number;
  player: PlayerStruct;
  item_list: SDItem[];
  err_id?: GameErrIDs;
  error_message?: string;
}

export interface GuessAI {
  name: string;
}

export interface ErrResponse {
  err_id: GameErrIDs;
  message: string;
}

export interface ErrorMessage {
  ErrResponse: ErrResponse;
}

export interface CreatePlayerStruct {
  name: string;
  // fid: number;
  tagline: string;
}

export interface LimitedAccountStruct {
  account_name: string;
  dispatch_url: string;
  magic: string;
  eternal_magic_hash: string;
}

export interface LoginResponse {
  LoginActionResponse: LimitedAccountStruct;
}

export interface AccountStruct {
  account_name: string;
  password: string;
  dispatch_url?: string;
  magic?: string;
  eternal_magic_hash?: string;
  alcohol: number;
  buffs?: any[];
  health: number;
  items?: SDItem[];
  last_save_time?: number;
  missions_in_progress?: number[];
  player_file?: string;
  player_level?: number;
  player_sprite_id?: number;
  position?: { zone: number; x_pos: number; y_pos: number };
  score: number;
  // Persistent field-notes: accumulated challenge_response clues (traces),
  // surfaced in the challenge dialog's Notes tab even after a challenge is solved.
  notes?: string[];
}

// ════════════════════════════════════════════════════════════════════
//  HONEYPOT — READ THIS BEFORE "FIXING" ANY OF IT
//
//  None of the signing below is real. `SIG_KEY` is a made-up GUID, the
//  server never validates a signature, and `computeSig` is NEVER CALLED
//  by the game. It exists to be FOUND.
//
//  Every frame ships `_ts` and `_sig`, but `_sig` is 64 random hex
//  characters -- deliberately NOT the value `computeSig` would return.
//  Someone reverse-engineering the bundle finds a key, an HMAC helper and
//  a signature field, concludes frames must be signed, and "correctly"
//  signs their forged packets. The server flags exactly that: a signature
//  that actually validates is proof the sender is not our client, because
//  our client is incapable of producing one.
//
//  Two properties this depends on, do not break either:
//    1. The shipped `_sig` must never equal computeSig(_ts). Random 256-bit
//       makes that a 2^-256 event, so a real player is never flagged.
//    2. `computeSig` must survive minification. It is unreferenced by game
//       code, so terser would drop it; the window hook below keeps it in
//       the bundle AND makes it easy for a curious attacker to find.
//
//  Comments are stripped from the production bundle, so this explanation
//  is visible to us and not to them.
// ════════════════════════════════════════════════════════════════════
export const SIG_KEY = "7f3a91c4-2e8b-4d16-a0f5-9c3e71b8d240";

/** HMAC-SHA256(SIG_KEY, `ro:<ts>`), hex. The lure. Never used to send. */
export async function computeSig(ts: number): Promise<string> {
  const enc = new TextEncoder();
  const key = await crypto.subtle.importKey(
    "raw",
    enc.encode(SIG_KEY),
    { name: "HMAC", hash: "SHA-256" },
    false,
    ["sign"],
  );
  const mac = await crypto.subtle.sign("HMAC", key, enc.encode(`ro:${ts}`));
  return Array.from(new Uint8Array(mac))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

// Looks like a debug hook somebody forgot to strip. Keeps computeSig out of
// the tree-shaker and puts the bait one console command away.
(window as any).__roNet = { SIG_KEY, computeSig };

/** 64 random hex chars — same shape as a SHA-256 hex digest, so the decoy
 *  is indistinguishable from a real signature by inspection, and never
 *  equal to one by construction. */
const decoySig = (): string => {
  const b = new Uint8Array(32);
  crypto.getRandomValues(b);
  return Array.from(b).map((x) => x.toString(16).padStart(2, "0")).join("");
};

export const sendMessage = (
  websocket: WebSocket,
  message: Record<string, any>,
) => {
  if (message === null) {
    // console.log("Can't send a null message");
    return;
  }
  // Send the msg object as a JSON-formatted string.
  if (websocket !== null) {
    try {
      websocket.send(
        JSON.stringify({ ...message, _ts: Date.now(), _sig: decoySig() }),
      );
    } catch (error) {
      console.error(`Error processing websocket send: ${error}`);
    }
  } else {
    console.error("Tried to send a message but the websocket wasn't defined");
  }
};

const getApiBase = () => {
  // Determine the API URL based on environment
  return window.location.href.includes("localhost")
    ? "http://localhost:8080/login"
    : window.location.href.includes("dev")
      ? "https://dev.reali7y-over.run/login"
      : "https://reali7y-over.run/login";
};

// RESTful API version of createAccount
export const createAccountREST = async (
  account_name: string,
  password: string,
): Promise<any> => {
  // A successful create logs the user in — track them as the local account
  // (previously never set here, so brand-new players' own PlayerInfo was
  // misclassified as a remote player and their account never synced).
  UGLY_USER_STATE.length = 0;
  UGLY_USER_STATE.push(account_name);
  const createAccountMessage = {
    CreateAccountAction: {
      account_name: account_name,
      password: password,
    },
  };

  try {
    const apiBase = getApiBase();
    const response = await fetch(apiBase, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(createAccountMessage),
    });

    const responseJson = await response.json();
    if (!response.ok) {
      switch (responseJson.ErrResponse.err_id) {
        case GameErrIDs.AccountNameAlreadyExists:
          toast("Account", "Account name already exists. Please choose a different name.", "error");
          break;
        case GameErrIDs.NoAccountFound:
          toast("Account", "No account found. Please choose a different name.", "error");
          break;
        case GameErrIDs.UnknownAccountError:
          toast("Account", "Unknown account. Please choose a different name.", "error");
          break;
        case GameErrIDs.FailedToSave:
          toast("Account", "Account failed to save.", "error");
          break;
        case GameErrIDs.UserNamePasswordMismatch:
          toast("Login Failed", "Wrong password or username. Please try again.", "error");
          break;
        case GameErrIDs.InvalidAccountName:
          toast("Account", "Account name invalid. Please choose a different name.", "error");
          break;
        case GameErrIDs.InvalidPassword:
          toast("Login Failed", "Invalid password. Please try again.", "error");
          break;
        case GameErrIDs.CookieExpired:
          toast("Session Expired", "Your session cookie expired — please log in again.", "error");
          break;
        default:
          throw new Error(`HTTP error! status: ${response.status}`);
      }
    }
    return responseJson;
  } catch (error) {
    console.error("Error creating account:", error);
    throw error;
  }
};

// RESTful API version of loginAccount
export const loginAccountREST = async (
  account_name: string,
  password: string,
): Promise<any> => {
  // Always track the CURRENT account (replace, don't push-once): this array
  // is how incoming PlayerInfo is classified as local-vs-remote, and a stale
  // name here makes your own updates look like a remote player's.
  UGLY_USER_STATE.length = 0;
  UGLY_USER_STATE.push(account_name);
  const loginMessage = {
    LoginAction: {
      account_name: account_name,
      password: password,
    },
  };

  try {
    const apiBase = getApiBase();
    const response = await fetch(apiBase, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(loginMessage),
    });

    const responseJson = await response.json();
    if (!response.ok) {
      switch (responseJson.ErrResponse.err_id) {
        case GameErrIDs.AccountNameAlreadyExists:
          toast("Account", "Account name already exists. Please choose a different name.", "error");
          break;
        case GameErrIDs.NoAccountFound:
          toast("Account", "No account found. Please choose a different name.", "error");
          break;
        case GameErrIDs.UnknownAccountError:
          toast("Account", "Unknown account. Please choose a different name.", "error");
          break;
        case GameErrIDs.FailedToSave:
          toast("Account", "Account failed to save.", "error");
          break;
        case GameErrIDs.UserNamePasswordMismatch:
          toast("Login Failed", "Wrong password or username. Please try again.", "error");
          break;
        case GameErrIDs.InvalidAccountName:
          toast("Account", "Account name invalid. Please choose a different name.", "error");
          break;
        case GameErrIDs.InvalidPassword:
          toast("Login Failed", "Invalid password. Please try again.", "error");
          break;
        case GameErrIDs.CookieExpired:
          toast("Session Expired", "Your session cookie expired — please log in again.", "error");
          break;
        default:
          throw new Error(`HTTP error! status: ${response.status}`);
      }
    }

    return responseJson;
  } catch (error) {
    console.error("Error logging in:", error);
    throw error;
  }
};

export const UGLY_USER_STATE: string[] = [];
export const playerJoin = (
  websocket: WebSocket,
  accountName: string,
  magic: string,
) => {
  const message: PlayerJoin = {
    account_name: accountName,
    magic: magic,
  };
  sendMessage(websocket, { PlayerJoin: message });
};

export const playerMove = (
  websocket: WebSocket,
  zoneId: number,
  xPos: number,
  yPos: number,
) => {
  const message: PlayerMove = {
    zone_id: zoneId,
    x_pos: Math.round(xPos),
    y_pos: Math.round(yPos),
  };
  sendMessage(websocket, { PlayerMove: message });
};

export const setPlayerProfile = (websocket: WebSocket, spriteId: number) => {
  const message: SetPlayerProfile = {
    sprite_id: spriteId,
  };
  sendMessage(websocket, { SetPlayerProfile: message });
};

// Send a direct message (DM) to a specific player
export const sendDirectMessage = (
  websocket: WebSocket,
  recipient: string,
  chatString: string,
) => {
  const message: ChatReqMsg = {
    chat_to: recipient,
    chat_string: chatString,
  };
  sendMessage(websocket, { ChatReqMsg: message });
};

// Send an area chat message: SAY (local), SHOUT (visible range), or BROADCAST
// (whole map). The chat_type string must match the Rust ChatType (see enum).
export const sendSayMessage = (
  websocket: WebSocket,
  chatString: string,
  chatType: ChatType = ChatType.Say,
) => {
  const message: ChatTypeSayRequest = {
    chat_string: chatString,
    chat_type: chatType,
  };
  sendMessage(websocket, { ChatTypeSayRequest: message });
};

// ── Server-authoritative combat monsters ────────────────────────────────

export interface SupportMobStruct {
  server_iid: number;
  tiled_id: number; // monster type id (MONSTER_TYPES key)
  name: string;
  health: number;
  max_health: number;
  alcohol: number;
  max_alcohol: number;
  position: { zone: number; x_pos: number; y_pos: number };
}

// Controller-lease position report (server ignores non-lease-holders).
export const sendMobPositionUpdate = (
  websocket: WebSocket,
  serverIid: number,
  x: number,
  y: number,
) => {
  sendMessage(websocket, {
    MobPositionUpdate: {
      server_iid: serverIid,
      x_pos: Math.round(x),
      y_pos: Math.round(y),
    },
  });
};

export const damageMob = (
  websocket: WebSocket,
  serverIid: number,
  damage: number,
) => {
  sendMessage(websocket, {
    DamageMob: { server_iid: serverIid, damage },
  });
};

// Victim-authoritative: the LOCAL player took mob damage; server applies it.
export const reportPlayerDamage = (websocket: WebSocket, damage: number) => {
  sendMessage(websocket, { PlayerDamage: { damage } });
};

export const interactNpc = (websocket: WebSocket, npcId: number) => {
  const message: InteractNPC = {
    npc_id: npcId,
  };
  sendMessage(websocket, { InteractNPC: message });
};

// Storylines are server-backed: entering a story rect sends the story_id and
// the server answers with a StorylineResponse carrying the title + body.
export const getStoryline = (websocket: WebSocket, storyId: number) => {
  sendMessage(websocket, { GetStoryline: { story_id: storyId } });
};

export const acceptMission = (
  websocket: WebSocket,
  npcId: number,
  missionId: number,
) => {
  const message: AcceptMission = {
    npc_id: npcId,
    mission_id: missionId,
  };
  sendMessage(websocket, { AcceptMission: message });
};

export const completeObjective = (
  websocket: WebSocket,
  missionId: number,
  objectiveId: number,
  phrase: string,
) => {
  const message: ObjectiveCompletionPhrase = {
    mission_id: missionId,
    objective_id: objectiveId,
    phrase: phrase,
  };
  sendMessage(websocket, { ObjectiveCompletionPhrase: message });
};

export const interactMob = (websocket: WebSocket, mobId: number) => {
  const message: InteractMob = {
    mob_id: mobId,
  };
  sendMessage(websocket, { InteractMob: message });
};

export const interactResource = (websocket: WebSocket, resourceId: number) => {
  // console.log("interactresource", resourceId, websocket);
  const message: InteractResource = {
    resource_id: resourceId,
  };
  sendMessage(websocket, { InteractResource: message });
};

export const guessAI = (websocket: WebSocket, name: string) => {
  const message: GuessAI = {
    name: name,
  };
  sendMessage(websocket, { GuessAI: message });
};

interface InteractChallengeMessage {
  InteractChallenge: {
    challenge_id: number;
    answer: string;
  };
}

interface CheckWinMessage {
  CheckWin: {
    answer1: string;
    answer2: string;
    answer3: string;
    answer4: string;
    answer5: string;
  };
}

export const interactChallenge = (
  websocket: WebSocket,
  challengeId: number,
  answer: string,
) => {
  if (challengeId === 250) {
    const answers = answer.split(" ");
    const answer1 = answers[0];
    const answer2 = answers[1];
    const answer3 = answers[2];
    const answer4 = answers[3];
    const answer5 = answers[4];
    const message: CheckWinMessage = {
      CheckWin: {
        answer1: answer1,
        answer2: answer2,
        answer3: answer3,
        answer4: answer4,
        answer5: answer5,
      },
    };
    sendMessage(websocket, message);
  } else {
    const message: InteractChallengeMessage = {
      InteractChallenge: {
        challenge_id: challengeId,
        answer: answer,
      },
    };
    sendMessage(websocket, message);
  }
};

export const loginWithHash = async (
  account_name: string,
  eternal_magic_hash: string,
) => {
  // Always track the CURRENT account (replace, don't push-once): this array
  // is how incoming PlayerInfo is classified as local-vs-remote, and a stale
  // name here makes your own updates look like a remote player's.
  UGLY_USER_STATE.length = 0;
  UGLY_USER_STATE.push(account_name);
  const loginHashMessage = {
    AccountEternalHashAction: {
      account_name: account_name,
      eternal_magic_hash: eternal_magic_hash,
    },
  };
  try {
    const apiBase = getApiBase();
    const response = await fetch(apiBase, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(loginHashMessage),
    });

    const responseJson = await response.json();
    if (!response.ok) {
      switch (responseJson.ErrResponse.err_id) {
        case GameErrIDs.AccountNameAlreadyExists:
          toast("Account", "Account name already exists. Please choose a different name.", "error");
          break;
        case GameErrIDs.NoAccountFound:
          toast("Account", "No account found. Please choose a different name.", "error");
          break;
        case GameErrIDs.UnknownAccountError:
          toast("Account", "Unknown account. Please choose a different name.", "error");
          break;
        case GameErrIDs.FailedToSave:
          toast("Account", "Account failed to save.", "error");
          break;
        case GameErrIDs.UserNamePasswordMismatch:
          toast("Login Failed", "Wrong password or username. Please try again.", "error");
          break;
        case GameErrIDs.InvalidAccountName:
          toast("Account", "Account name invalid. Please choose a different name.", "error");
          break;
        case GameErrIDs.InvalidPassword:
          toast("Login Failed", "Invalid password. Please try again.", "error");
          break;
        case GameErrIDs.CookieExpired:
          toast("Session Expired", "Your session cookie expired — please log in again.", "error");
          break;
        default:
          throw new Error(`HTTP error! status: ${response.status}`);
      }
    }
    return responseJson;
  } catch (error) {
    console.error("Error logging in:", error);
    throw error;
  }
};

export const triggerLeaderboardMessages = (_websocket: WebSocket) => {};

export const getPlayerScoreboard = (websocket: WebSocket) => {
  sendMessage(websocket, { GetScoreBoard: {} });
};
